import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { coursesApi, CreateCourseDto, UpdateCourseDto } from '../lib/api/courses';

export const COURSES_QUERY_KEY = 'courses';

export function useCourses() {
  return useQuery({
    queryKey: [COURSES_QUERY_KEY],
    queryFn: coursesApi.getAll,
  });
}

export function useCourse(id: number) {
  return useQuery({
    queryKey: [COURSES_QUERY_KEY, id],
    queryFn: () => coursesApi.getById(id),
    enabled: !!id,
  });
}

export function useCourseMembers(id: number) {
  return useQuery({
    queryKey: [COURSES_QUERY_KEY, id, 'members'],
    queryFn: () => coursesApi.getMembers(id),
    enabled: !!id,
  });
}

export function useCreateCourse() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (dto: CreateCourseDto) => coursesApi.create(dto),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [COURSES_QUERY_KEY] });
    },
  });
}

export function useUpdateCourse() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, dto }: { id: number; dto: UpdateCourseDto }) =>
      coursesApi.update(id, dto),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: [COURSES_QUERY_KEY] });
      queryClient.invalidateQueries({ queryKey: [COURSES_QUERY_KEY, variables.id] });
    },
  });
}

export function useDeleteCourse() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: number) => coursesApi.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [COURSES_QUERY_KEY] });
    },
  });
}

export function useJoinCourse() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: number) => coursesApi.join(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [COURSES_QUERY_KEY] });
    },
  });
}

export function useLeaveCourse() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: number) => coursesApi.leave(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [COURSES_QUERY_KEY] });
    },
  });
}

export function useGrantEditor() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ courseId, userId }: { courseId: number; userId: number }) =>
      coursesApi.grantEditor(courseId, userId),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({
        queryKey: [COURSES_QUERY_KEY, variables.courseId, 'members'],
      });
    },
  });
}

export function useRevokeEditor() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ courseId, userId }: { courseId: number; userId: number }) =>
      coursesApi.revokeEditor(courseId, userId),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({
        queryKey: [COURSES_QUERY_KEY, variables.courseId, 'members'],
      });
    },
  });
}

export function useRemoveMember() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ courseId, userId }: { courseId: number; userId: number }) =>
      coursesApi.removeMember(courseId, userId),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({
        queryKey: [COURSES_QUERY_KEY, variables.courseId, 'members'],
      });
    },
  });
}

export function useInviteMember() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ courseId, nickname }: { courseId: number; nickname: string }) =>
      coursesApi.inviteMember(courseId, nickname),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: [COURSES_QUERY_KEY, variables.courseId, 'members'] });
      queryClient.invalidateQueries({ queryKey: [COURSES_QUERY_KEY] });
    },
  });
}
